// PrintControl.tsx
import { useEffect, useMemo, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

type PrintControlProps = {
  onBeforePrint?: () => void;
  onAfterPrint?: () => void;
  title?: string;               // bouton impression
  icon?: string;                // html/icône impression
  position?: L.ControlPosition;
  offsetTop?: number;
  orientation?: "landscape" | "portrait";
  pageSize?: string;            // "A4" (par défaut), "A3", "Letter", "Legal"…
  extraPrintCss?: string;

  /** comment recadrer avant impression */
  fitOnPrint?: "world" | "keep" | "selection" | L.LatLngBoundsExpression;

  /** sélection rectangle intégrée */
  selectionEnabled?: boolean;
  selectionTitle?: string;      // tooltip bouton sélection
  selectionIcon?: string;       // html/icône bouton sélection
  selectionStyle?: L.PathOptions;

  /** bouton pour supprimer la sélection */
  selectionDeleteTitle?: string;
  selectionDeleteIcon?: string;
};

export default function PrintControl({
  onBeforePrint,
  onAfterPrint,
  title = "Imprimer la carte",
  icon = '<i class="las la-print"></i>',
  position = "topright",
  offsetTop = 200,
  orientation = "landscape",
  pageSize = "A4",
  extraPrintCss,
  fitOnPrint = "world",
  selectionEnabled = true,
  selectionTitle = "Sélectionner une zone",
  selectionIcon = '<i class="las la-vector-square"></i>',
  selectionStyle,
  selectionDeleteTitle = "Supprimer la sélection",
  selectionDeleteIcon = '<i class="las la-trash-alt"></i>',
}: PrintControlProps) {
  const map = useMap();
  const styleRef = useRef<HTMLStyleElement | null>(null);

  // Sélection (Leaflet.Draw)
  const drawHandlerRef = useRef<any>(null);
  const selectedLayerRef = useRef<L.Rectangle | null>(null);
  const selectedBoundsRef = useRef<L.LatLngBounds | null>(null);
  const onCreatedRef = useRef<((e: any) => void) | null>(null);

  /** Dimensions standard en mm (A-séries ISO, Letter/Legal US) */
  const pageSizesMm: Record<string, { w: number; h: number }> = {
    A0: { w: 841, h: 1189 },
    A1: { w: 594, h: 841 },
    A2: { w: 420, h: 594 },
    A3: { w: 297, h: 420 },
    A4: { w: 210, h: 297 },
    A5: { w: 148, h: 210 },
    Letter: { w: 216, h: 279 },
    Legal: { w: 216, h: 356 },
  };

  /** Calcule le ratio (largeur/hauteur) du contenu imprimable (après marges) */
  const getPrintableRatio = (size: string, orient: "landscape" | "portrait", marginMm = 6) => {
    const base = pageSizesMm[size] ?? pageSizesMm["A4"];
    const w = orient === "landscape" ? Math.max(base.w, base.h) : Math.min(base.w, base.h);
    const h = orient === "landscape" ? Math.min(base.w, base.h) : Math.max(base.w, base.h);
    const contentW = Math.max(10, w - 2 * marginMm);
    const contentH = Math.max(10, h - 2 * marginMm);
    return contentW / contentH;
  };

  /** Ajuste des bounds sélectionnés à l’aspect ratio cible de la page */
  const adjustBoundsToRatio = (b: L.LatLngBounds, ratioTarget: number): L.LatLngBounds => {
    const z = map.getZoom();
    // Projeter SW / NE en pixels au zoom courant
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    const pSW = map.project(sw, z);
    const pNE = map.project(ne, z);

    let x1 = pSW.x, y1 = pNE.y; // coin haut-gauche
    let x2 = pNE.x, y2 = pSW.y; // coin bas-droit

    const wPx = x2 - x1;
    const hPx = y2 - y1;
    if (wPx <= 0 || hPx <= 0) return b;

    const current = wPx / hPx;
    let nx1 = x1, ny1 = y1, nx2 = x2, ny2 = y2;

    if (Math.abs(current - ratioTarget) > 1e-3) {
      if (current < ratioTarget) {
        // pas assez large → on élargit
        const newW = hPx * ratioTarget;
        const delta = (newW - wPx) / 2;
        nx1 = x1 - delta;
        nx2 = x2 + delta;
      } else {
        // trop large → on augmente la hauteur
        const newH = wPx / ratioTarget;
        const delta = (newH - hPx) / 2;
        ny1 = y1 - delta;
        ny2 = y2 + delta;
      }
    }

    // petit padding de sécurité (2px)
    nx1 -= 2; ny1 -= 2; nx2 += 2; ny2 += 2;

    const nSW = map.unproject(L.point(nx1, ny2), z);
    const nNE = map.unproject(L.point(nx2, ny1), z);
    return L.latLngBounds(nSW, nNE);
  };

  /** CSS print fiable (mm, 1 page, neutralise transform) */
  const injectPrintCss = () => {
    const marginMm = 6;
    const base = pageSizesMm[pageSize] ?? pageSizesMm["A4"];
    const w = orientation === "landscape" ? Math.max(base.w, base.h) : Math.min(base.w, base.h);
    const h = orientation === "landscape" ? Math.min(base.w, base.h) : Math.max(base.w, base.h);
    const contentW = `${w - marginMm * 2}mm`;
    const contentH = `${h - marginMm * 2}mm`;

    const css = `
      @page { size: ${pageSize} ${orientation}; margin: ${marginMm}mm; }

      @media print {
        html, body { height: 100% !important; overflow: hidden !important; }
        .leaflet-container { width: ${contentW} !important; height: ${contentH} !important; }
        .print-map        { width: ${contentW} !important; height: ${contentH} !important; }

        .leaflet-pane,
        .leaflet-map-pane,
        .leaflet-tile-pane,
        .leaflet-overlay-pane,
        .leaflet-shadow-pane,
        .leaflet-marker-pane,
        .leaflet-tooltip-pane,
        .leaflet-popup-pane {
          transform: none !important;
          will-change: auto !important;
        }
        /* .leaflet-control-container, .ri-toolbar { display: none !important; } */
      }

      ${extraPrintCss ?? ""}
    `.trim();

    const style = document.createElement("style");
    style.setAttribute("type", "text/css");
    style.setAttribute("media", "print");
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
    styleRef.current = style;
  };

  const removePrintCss = () => {
    if (styleRef.current) {
      styleRef.current.remove();
      styleRef.current = null;
    }
  };

  /** Lance le mode sélection rectangle (sans toolbar complète) */
  const startSelection = () => {
    if (!drawHandlerRef.current) {
      const Rectangle = (L as any).Draw?.Rectangle;
      if (!Rectangle) return;

      drawHandlerRef.current = new Rectangle(map, {
        shapeOptions: { color: "#0ea5e9", weight: 2, fill: false, ...(selectionStyle || {}) },
      });

      onCreatedRef.current = (e: any) => {
        const layer = e.layer as L.Rectangle;
        if (!layer) return;

        // supprime l’ancienne sélection
        if (selectedLayerRef.current) {
          try { map.removeLayer(selectedLayerRef.current); } catch {}
        }
        selectedLayerRef.current = layer.addTo(map);
        selectedBoundsRef.current = layer.getBounds();

        // clic sur le rectangle = le sélectionne (utile si tu veux évoluer)
        try {
          layer.on("click", () => { /* placeholder si besoin */ });
        } catch {}

        // recadre pour la visualisation
        try { map.fitBounds(selectedBoundsRef.current!, { padding: [8, 8], animate: false }); } catch {}
      };

      map.on((L as any).Draw.Event.CREATED, onCreatedRef.current);
    }
    try { drawHandlerRef.current.enable(); } catch {}
  };

  /** Supprime la sélection */
  const clearSelection = () => {
    if (selectedLayerRef.current) {
      try { map.removeLayer(selectedLayerRef.current); } catch {}
      selectedLayerRef.current = null;
    }
    selectedBoundsRef.current = null;
  };

  /** Impression */
  const handlePrint = useMemo(
    () => () => {
      const prev = { center: map.getCenter(), zoom: map.getZoom() };

      try {
        if (fitOnPrint === "selection") {
          const b = selectedBoundsRef.current;
          if (b) {
            // Ajuster aux proportions exactes de la page imprimable
            const ratio = getPrintableRatio(pageSize, orientation, 6);
            const adjusted = adjustBoundsToRatio(b, ratio);
            map.fitBounds(adjusted, { padding: [0, 0], animate: false });
          }
        } else if (fitOnPrint === "world") {
          map.fitWorld({ animate: false });
        } else if (fitOnPrint !== "keep") {
          map.fitBounds(fitOnPrint as L.LatLngBoundsExpression, { padding: [0, 0], animate: false });
        }
      } catch {}

      try { onBeforePrint?.(); } catch {}
      injectPrintCss();

      setTimeout(() => {
        try {
          map.invalidateSize(true);
          map.eachLayer((layer: any) => {
            if (layer && typeof layer.redraw === "function") {
              try { layer.redraw(); } catch {}
            }
          });
        } catch {}

        const cleanup = () => {
          try { map.setView(prev.center, prev.zoom, { animate: false }); } catch {}
          try { onAfterPrint?.(); } catch {}
          removePrintCss();
          window.removeEventListener("afterprint", cleanup);
        };

        window.addEventListener("afterprint", cleanup);
        setTimeout(() => window.print(), 180);
      }, 120);
    },
    [map, onBeforePrint, onAfterPrint, fitOnPrint, pageSize, orientation, extraPrintCss]
  );

  useEffect(() => {
    class LeafletPrintControl extends L.Control {
      constructor(opts?: L.ControlOptions) { super(opts); }

      onAdd(_: L.Map) {
        const container = L.DomUtil.create("div", "leaflet-control leaflet-bar printctl");

        // Bouton PRINT
        const btnPrint = L.DomUtil.create("a", "printctl-btn", container);
        btnPrint.href = "#";
        btnPrint.role = "button";
        btnPrint.tabIndex = 0;
        btnPrint.setAttribute("title", title || "Imprimer");
        btnPrint.setAttribute("aria-label", title || "Imprimer");
        btnPrint.innerHTML = icon || '<i class="las la-print"></i>';

        // Bouton SELECT
        let btnSelect: HTMLAnchorElement | null = null;
        if (selectionEnabled) {
          btnSelect = L.DomUtil.create("a", "printctl-select-btn", container);
          btnSelect.href = "#";
          btnSelect.role = "button";
          btnSelect.tabIndex = 0;
          btnSelect.setAttribute("title", selectionTitle);
          btnSelect.setAttribute("aria-label", selectionTitle);
          btnSelect.innerHTML = selectionIcon;
        }

        // Bouton DELETE
        const btnDelete = L.DomUtil.create("a", "printctl-delete-btn", container);
        btnDelete.href = "#";
        btnDelete.role = "button";
        btnDelete.tabIndex = 0;
        btnDelete.setAttribute("title", selectionDeleteTitle);
        btnDelete.setAttribute("aria-label", selectionDeleteTitle);
        btnDelete.innerHTML = selectionDeleteIcon;

        if ((position as string).startsWith("top") && offsetTop > 0) {
          container.style.top = `${offsetTop}px`;
        }

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        L.DomEvent.on(btnPrint, "click", (e: any) => { L.DomEvent.stop(e); handlePrint(); });
        btnPrint.addEventListener("keydown", (e) => {
          const k = e as KeyboardEvent; if (k.key === "Enter" || k.key === " ") { k.preventDefault(); handlePrint(); }
        });

        if (btnSelect) {
          L.DomEvent.on(btnSelect, "click", (e: any) => { L.DomEvent.stop(e); startSelection(); });
          btnSelect.addEventListener("keydown", (e) => {
            const k = e as KeyboardEvent; if (k.key === "Enter" || k.key === " ") { k.preventDefault(); startSelection(); }
          });
        }

        L.DomEvent.on(btnDelete, "click", (e: any) => { L.DomEvent.stop(e); clearSelection(); });
        btnDelete.addEventListener("keydown", (e) => {
          const k = e as KeyboardEvent; if (k.key === "Enter" || k.key === " ") { k.preventDefault(); clearSelection(); }
        });

        // Touche Delete supprime la sélection
        const onKeyDown = (ev: KeyboardEvent) => {
          if (ev.key === "Delete" || ev.key === "Backspace") { clearSelection(); }
        };
        document.addEventListener("keydown", onKeyDown);

        // Nettoyage listener clavier à la suppression du contrôle
        (container as any).__cleanupKeyDown__ = onKeyDown;

        return container;
      }

      onRemove(_: L.Map) {
        // retiré dans le cleanup useEffect
      }
    }

    const ctrl = new LeafletPrintControl({ position });
    ctrl.addTo(map);

    return () => {
      // Nettoyage
      // @ts-ignore
      const container = (ctrl as any)._container as HTMLElement | undefined;
      if (container && (container as any).__cleanupKeyDown__) {
        document.removeEventListener("keydown", (container as any).__cleanupKeyDown__);
      }
      ctrl.remove();
      removePrintCss();

      if (onCreatedRef.current) {
        try { map.off((L as any).Draw.Event.CREATED, onCreatedRef.current); } catch {}
      }
      if (drawHandlerRef.current) {
        try { drawHandlerRef.current.disable(); } catch {}
        drawHandlerRef.current = null;
      }
      if (selectedLayerRef.current) {
        try { map.removeLayer(selectedLayerRef.current); } catch {}
        selectedLayerRef.current = null;
      }
      selectedBoundsRef.current = null;
    };
  }, [map, position, title, icon, handlePrint, offsetTop, selectionEnabled, selectionIcon, selectionTitle, selectionStyle, selectionDeleteIcon, selectionDeleteTitle]);

  return null;
}
